import { ReactElement, useState } from 'react';
import clsx from 'clsx';
import Icon, { IconType } from '@components/Icon';
import { slugify } from '@lib/utils';
import {
  StyledIconLegend,
  StyledIconLegendDescription,
  StyledIconLegendGrid,
  StyledIconLegendInfo,
  StyledIconLegendItem,
  StyledIconLegendLabel,
  StyledIconLegendToggle,
  StyledIconLegendWrapper,
} from './IconLegend.styles';

export type IconLegendItem = {
  type: IconType | '';
  label: string;
  info?: string;
};

export type IconLegendProps = {
  items: IconLegendItem[];
  className?: string;
  toggle?: boolean;
  toggleLabel?: string;
  toggleItem?: string;
  grid?: number;
  iconWidth?: number;
  iconHeight?: number;
  ariaLabel?: string;
};

const renderItemContent = ({ type, label, info }: IconLegendItem, iconWidth?: number, iconHeight?: number) => (
  <>
    {type && <Icon type={type} ariaHidden={true} width={iconWidth} height={iconHeight} useCurrentColor={true} />}
    <StyledIconLegendDescription>
      <StyledIconLegendLabel dangerouslySetInnerHTML={{ __html: label }} />
      {info && <StyledIconLegendInfo dangerouslySetInnerHTML={{ __html: info }} />}
    </StyledIconLegendDescription>
  </>
);

export default function IconLegend({ items, className, toggle, toggleLabel = "What's on key", toggleItem, grid, iconWidth, iconHeight, ariaLabel }: IconLegendProps): ReactElement {
  const [isOpen, setIsOpen] = useState(() => !toggle);

  const toggleItemData = toggleItem ? items.find(({ label }) => slugify(label) === toggleItem) : undefined;
  const listItems = toggle && toggleItemData ? items.filter(({ label }) => slugify(label) !== toggleItem) : items;
  const showToggle = toggle && (toggleItemData ? true : !!toggleLabel);
  const listClassName = toggle && isOpen ? 'is-open' : undefined;

  const renderItems = () => listItems.map((item) => (
    <StyledIconLegendItem key={item.label} className={clsx({ 'with-icon': !!item.type })}>
      {renderItemContent(item, iconWidth, iconHeight)}
    </StyledIconLegendItem>
  ));

  return (
    <StyledIconLegendWrapper
      className={clsx(className, {
        'with-toggle': toggle,
      })}
      aria-label={ariaLabel ?? toggleLabel}
    >
      {showToggle ? (
        <StyledIconLegendToggle
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          className={clsx({ 'with-icon': !!toggleItemData?.type })}
        >
          {toggleItemData ? renderItemContent(toggleItemData, iconWidth, iconHeight) : toggleLabel}
        </StyledIconLegendToggle>
      ) : null}
      {!toggle || isOpen ? (
        grid ? (
          <StyledIconLegendGrid $columns={grid} className={listClassName} aria-label={ariaLabel ?? toggleLabel}>
            {renderItems()}
          </StyledIconLegendGrid>
        ) : (
          <StyledIconLegend className={listClassName} aria-label={ariaLabel ?? toggleLabel}>
            {renderItems()}
          </StyledIconLegend>
        )
      ) : null}
    </StyledIconLegendWrapper>
  );
}
